import cn from "classnames";
import { KeyboardEvent, ReactNode, useState } from "react";

import styles from "./ChipEditor.module.scss";

const PlusBox = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="2" y="2" width="16" height="16" rx="4" stroke="#7E7E8B" strokeWidth="1.5" fill="none" />
    <path d="M10 6v8M6 10h8" stroke="#7E7E8B" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CrossIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M3 3l6 6m0-6l-6 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

type ChipVariant = "positive" | "negative" | "neutral";

export interface ChipItem {
  id: string;
  label: string;
}

interface Props {
  title?: ReactNode;
  subtitle?: string;
  placeholder: string;
  variant: ChipVariant;
  items: ChipItem[];
  disabled?: boolean;
  onAdd: (text: string) => void;
  onRemove: (id: string) => void;
}

const CHIP_CLASS: Record<ChipVariant, string> = {
  positive: styles.chipPositive,
  negative: styles.chipNegative,
  neutral: styles.chipNeutral
};

const ChipEditor = ({
  title,
  subtitle,
  placeholder,
  variant,
  items,
  disabled,
  onAdd,
  onRemove
}: Props) => {
  const [input, setInput] = useState("");

  const add = () => {
    const trimmed = input.trim();
    if (!trimmed || disabled) return;
    onAdd(trimmed);
    setInput("");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      add();
    }
  };

  return (
    <div className={styles.section}>
      {title && <div className={styles.sectionTitle}>{title}</div>}
      {subtitle && <div className={styles.sectionSub}>{subtitle}</div>}

      <div className={styles.inputRow}>
        <input
          className={styles.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
        />
        <button
          type="button"
          className={styles.addBtn}
          onClick={add}
          disabled={disabled || input.trim().length === 0}
        >
          <PlusBox />
        </button>
      </div>

      {items.length > 0 && (
        <div className={styles.chips}>
          {items.map((item) => (
            <span
              key={item.id}
              className={cn(styles.chip, CHIP_CLASS[variant])}
            >
              <span className={styles.chipText}>{item.label}</span>
              <button
                type="button"
                className={styles.chipClose}
                disabled={disabled}
                onClick={() => onRemove(item.id)}
                aria-label="Remove"
              >
                <CrossIcon />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChipEditor;
